//new slice with owner id 

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../config/axiosConfig';
import config from '../../config/api';
import {
  buildTrainingExpensePayloadRow,
  collapseTrainingBundlesToSingleRowPerAssignee,
  isTrainingExpenseEntry,
  mergeTrainingExpenseRowsInto,
  normalizeDescriptionTaskWireEntry,
  resolveErpProjectIdForTraining,
  resolveTrainingAssigneeId,
  unwrapTrainings,
  canBeAssigneeForMergedTrainingExpenseRows,
} from '../../utils/trainingOtherExpenseSync';

const API_URL = `${config.backendUrl}/descriptiontask`;

export const fetchDescriptionTasks = createAsyncThunk(
  'descriptionTask/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${API_URL}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createDescriptionTasks = createAsyncThunk(
  'descriptionTask/create',
  async (taskArray, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`${API_URL}`, taskArray);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const replaceAllDescriptionTasks = createAsyncThunk(
  'descriptionTask/replaceAll',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`${API_URL}/saveupdate`, payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Persist My Trainings amounts into Other Expenses (description tasks). Non-Hardware flows may keep
 * one row per training; Hardware projects collapses rows per OPS assignee to a single summed
 * Training Cost line (see collapseTrainingBundlesToSingleRowPerAssignee).
 *
 * Optionally pass `{ extraPairs: [{ assigneeEmpId, erpProjectId }] }` when assignee/project
 * changed server-side so the previous bucket is rewritten too.
 */
export const reconcileTrainingLinkedOtherExpenses = createAsyncThunk(
  'descriptionTask/reconcileTrainingLinkedOtherExpenses',
  async ({ extraPairs = [] } = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const tasks = ([]).concat(state.descriptionTask?.tasks || []);
      const employeesList = state.employees?.employees || [];
      const projectsList = state.projects?.projects || [];
      const trainingsListRaw = unwrapTrainings(state.myTrainings?.mytrainings);

      const pairMap = new Map();

      /** @type {{ assigneeEmpId: string; erpProjectId: string }[]} */
      const extras = ([]).concat(extraPairs || []).filter(Boolean);

      for (const raw of trainingsListRaw) {
        const assigneeEmpId = resolveTrainingAssigneeId(raw?.assignedto ?? raw?.assignedTo);
        const empRecord = employeesList.find((e) => String(e?._id) === String(assigneeEmpId));
        if (!assigneeEmpId || !empRecord || !canBeAssigneeForMergedTrainingExpenseRows(empRecord)) continue;
        const erpProjectId = resolveErpProjectIdForTraining(raw, projectsList);
        if (!erpProjectId) continue;
        pairMap.set(`${String(assigneeEmpId)}:::${erpProjectId}`, {
          assigneeEmpId,
          erpProjectId,
        });
      }

      extras.forEach((p) => {
        const ai = String(p?.assigneeEmpId ?? p?.assignedto ?? "").trim();
        const pj = String(p?.erpProjectId ?? p?.projectId ?? p?.projectid ?? "").trim();
        if (!ai || !pj) return;
        const empRecord = employeesList.find((e) => String(e?._id) === String(ai));
        if (!empRecord || !canBeAssigneeForMergedTrainingExpenseRows(empRecord)) return;
        pairMap.set(`${ai}:::${pj}`, { assigneeEmpId: ai, erpProjectId: pj });
      });

      if (pairMap.size === 0) {
        return 0;
      }

      for (const pair of pairMap.values()) {
        const { assigneeEmpId, erpProjectId } = pair;

        const bundles = trainingsListRaw
          .filter((tr) => {
            const emp = resolveTrainingAssigneeId(tr?.assignedto ?? tr?.assignedTo);
            const pid = resolveErpProjectIdForTraining(tr, projectsList);
            return String(emp) === String(assigneeEmpId) && String(pid) === String(erpProjectId);
          })
          .map((tr) => buildTrainingExpensePayloadRow(tr, employeesList, projectsList))
          .filter(Boolean)
          .filter((b) => String(b.assigneeEmpId) === String(assigneeEmpId));

        const scopedTasks = tasks.filter((task) => {
          return (
            String(task.taskownerid) === String(assigneeEmpId) &&
            String(task.projectid).toLowerCase() === String(erpProjectId).toLowerCase()
          );
        });

        if (bundles.length === 0 && scopedTasks.length === 0) continue;

        const manuals = scopedTasks.filter((t) => !isTrainingExpenseEntry(t));

        const manualWiresNonTraining = manuals
          .map((row) => normalizeDescriptionTaskWireEntry(row))
          .filter(Boolean);

        const proj = projectsList.find((p) => String(p?._id) === String(erpProjectId));
        const ts = proj?.TypeService;
        const projIsHardware =
          ts === "Hardware" ||
          (Array.isArray(ts) &&
            ts.some((x) => String(x || "").trim() === "Hardware"));

        const bundlesMerged = projIsHardware
          ? collapseTrainingBundlesToSingleRowPerAssignee(bundles)
          : bundles;

        const mergedFlat = mergeTrainingExpenseRowsInto(manualWiresNonTraining, bundlesMerged);

        let defaultType =
          bundlesMerged[0]?.typeservice ??
          bundles[0]?.typeservice ??
          manuals.find(Boolean)?.typeservice ??
          manuals.find(Boolean)?.TypeService ??
          "";

        defaultType =
          typeof defaultType === "string"
            ? defaultType
            : ([]).concat(defaultType || []).find(Boolean);
        defaultType = String(defaultType ?? "").trim();

        const entries = mergedFlat.map((row) =>
          normalizeDescriptionTaskWireEntry({
            ...row,
            typeservice: row.typeservice ?? defaultType,
          })
        );

        await dispatch(
          replaceAllDescriptionTasks({
            projectid: erpProjectId,
            taskownerid: assigneeEmpId,
            tasktype: "other",
            entries,
          })
        ).unwrap();
      }

      await dispatch(fetchDescriptionTasks()).unwrap();
      return pairMap.size;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || String(err));
    }
  }
);

export const replaceByProjectWithCreatedBy = createAsyncThunk(
  'descriptionTask/replaceByProject',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`${API_URL}/replace-by-project`, payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateDescriptionTask = createAsyncThunk(
  'descriptionTask/update',
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`${API_URL}/update/${id}`, updateData);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteDescriptionTask = createAsyncThunk(
  'descriptionTask/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${API_URL}/delete/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const descriptionTaskSlice = createSlice({
  name: 'descriptionTask',
  initialState: {
    tasks: [],
    loading: false,
    error: null,
  },
  reducers: {
    resetDescriptionTaskState: (state) => {
      state.tasks = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDescriptionTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDescriptionTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchDescriptionTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(createDescriptionTasks.fulfilled, (state, action) => {
        state.tasks.push(...action.payload);
      })
      .addCase(replaceAllDescriptionTasks.fulfilled, (state, action) => {
      })
      .addCase(replaceByProjectWithCreatedBy.fulfilled, (state, action) => {
      })

      .addCase(updateDescriptionTask.fulfilled, (state, action) => {
      })
      
      .addCase(deleteDescriptionTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter(task => task._id !== action.payload);
      });
  }
});

export const { resetDescriptionTaskState } = descriptionTaskSlice.actions;

export default descriptionTaskSlice.reducer;
